// ==========================================
// FILE: src/utils/managerActions.js
// Universal helpers for manager read-only actions
// Used by: PersonalMessages, Videos, Devices (and can be reused elsewhere)
// ==========================================
import { auth, storage } from "../firebase";
import { ref as storageRef, getDownloadURL } from "firebase/storage";

const isHttpUrl = (u) => typeof u === "string" && /^https?:\/\//i.test(u);

/** Normalize possible storage references into a storage path */
export function toStoragePath(maybe) {
  if (!maybe || typeof maybe !== "string") return null;
  if (maybe.startsWith("gs://")) {
    const no = maybe.replace(/^gs:\/\//, "");
    const slash = no.indexOf("/");
    return slash >= 0 ? no.slice(slash + 1) : null;
  }
  if (isHttpUrl(maybe)) {
    const m = maybe.match(/\/o\/([^?]+)/);
    if (!m) return null;
    try {
      return decodeURIComponent(m[1]);
    } catch {
      return m[1];
    }
  }
  if (maybe.includes("..")) return null; // safety
  return maybe; // already a storage path
}

async function forceDownloadUrl(url, filename) {
  try {
    const res = await fetch(url);
    if (!res.ok) return false;
    const blob = await res.blob();
    const obj = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = obj;
    if (filename) a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(obj), 1000);
    return true;
  } catch {
    return false;
  }
}

/** Get a short-lived signed URL for managers via your CF endpoint */
export async function getSignedUrl(ownerUid, pathOrUrl, opts = {}) {
  const storagePath = isHttpUrl(pathOrUrl) ? toStoragePath(pathOrUrl) : (pathOrUrl || "");
  if (!storagePath) return null;

  try {
    const idToken = await auth.currentUser.getIdToken();
    const resp = await fetch("/api/accountManagers/getDownloadUrl", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
      // Cloud Function expects `storagePath`
      body: JSON.stringify({
        ownerUid,
        storagePath,
        filename: opts?.filename || "",
      }),
    });
    if (!resp.ok) return null;
    const data = await resp.json().catch(() => ({}));
    return data?.url || null;
  } catch {
    return null;
  }
}

/**
 * Download an attachment.
 *
 * - If `pathOrUrl` is already an https URL (e.g. a Firebase Storage downloadURL),
 *   we try to download it directly first (works for owners and managers).
 * - Otherwise, we try a signed URL (manager) and fall back to Storage getDownloadURL (owner).
 *
 * `fallbackPathOrUrl` lets callers provide a second option (e.g. storagePath + mediaUrl).
 */
export async function openDownloadForAttachment({
  ownerUid,
  pathOrUrl,
  fallbackPathOrUrl,
  suggestedName,
}) {
  const candidates = [pathOrUrl, fallbackPathOrUrl].filter(Boolean);

  for (const cand of candidates) {
    // 1) Direct URL download (fast path)
    if (isHttpUrl(cand)) {
      const ok = await forceDownloadUrl(cand, suggestedName);
      if (ok) return true;
      // If it failed (rare), continue to signed/storage attempts below.
    }

    // 2) Signed URL (manager) or Storage download URL (owner)
    const storagePath = toStoragePath(cand) || cand;
    if (!storagePath) continue;

    // Signed URL first (helps when Storage rules block getDownloadURL for managers)
    let url = await getSignedUrl(ownerUid, storagePath, { filename: suggestedName });
    if (!url) {
      try {
        url = await getDownloadURL(storageRef(storage, storagePath));
      } catch {
        url = null;
      }
    }
    if (!url) continue;

    const ok = await forceDownloadUrl(url, suggestedName);
    if (ok) return true;

    // Ultimate fallback: open in a new tab
    try {
      const a = document.createElement("a");
      a.href = url;
      if (suggestedName) a.download = suggestedName;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
      return true;
    } catch {
      // keep looping
    }
  }

  return false;
}

/** Download plain text as a .txt file (for message bodies, etc.) */
export function downloadTextFile(filename, content) {
  const blob = new Blob([content || ""], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "message.txt";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Build a mailto link that includes all fields plus an attachment URL
 * (mailto can’t attach files; we include a link instead).
 */
export async function buildForwardMailto({ ownerUid, pathOrUrl, subject, fields }) {
  let fileUrl = "";

  // Prefer a direct URL if we already have one (works for owners and managers)
  if (isHttpUrl(pathOrUrl)) fileUrl = pathOrUrl;

  // Otherwise, try to sign/get a URL from storage
  if (!fileUrl && pathOrUrl) {
    const path = toStoragePath(pathOrUrl) || pathOrUrl;
    fileUrl = (await getSignedUrl(ownerUid, path)) || "";
  }

  const bodyLines = [];
  Object.entries(fields || {}).forEach(([label, value]) => {
    if (value == null || value === "") return;
    bodyLines.push(`${label}: ${String(value)}`);
  });
  if (fileUrl) bodyLines.push(`Attachment link: ${fileUrl}`);

  const sub = encodeURIComponent(subject || "Message");
  const body = encodeURIComponent(bodyLines.join("\n\n"));
  return `mailto:?subject=${sub}&body=${body}`;
}
